import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "moment/locale/pt";
import "moment/locale/es";
import { LuRepeat2, LuCalendarPlus, LuCalendarX2 } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { fetchRestaurantOpenDays, fetchOpenClosedExceptions } from "../../actions/restaurantActions";
import TimeManagement from "./RegularShiftManagement";
import OpenClosedExceptions from "./OpenClosedExceptions";
import { ScheduleCalendar, ShiftActions, ShiftButton, scheduleEventAppearance } from "./scheduleStyles";

import { CustomModal } from "../CustomModal";
import CalendarCustomToolbar from "./CalendarCustomToolbar";
const localizer = momentLocalizer(moment);
export function ScheduleEvent({ event }) {
  const [heading, ...details] = event.title.split("\n");
  return <span title={event.title}>
    <span className="schedule-event-title">{heading}</span>
    {details.length > 0 && <span className="schedule-event-detail">{details.join(" · ")}</span>}
  </span>;
}
const RestaurantCalendarOpenClosedDays = ({
  restaurant_id
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    openDays,
    isLoadingOpenDays
  } = useSelector(state => state.restaurantOpenDaysOfTheWeek);
  const openClosedExceptions = useSelector(state => state.openClosedExceptions);
  const {
    exceptions
  } = openClosedExceptions;
  const [events, setEvents] = useState([]);
  const [selectedOption, setSelectedOption] = useState("regular_shift");
  const [mode, setMode] = useState("create");
  const [selected_obj_id, setSelected_obj_id] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(moment());
  const [calendarView, setCalendarView] = useState("month");
  const calendarContainerRef = React.useRef(null);
  const [calendarHeight, setCalendarHeight] = useState(640);
  const calendarLanguage = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  useEffect(() => {
    moment.locale(calendarLanguage === "pt" ? "pt" : calendarLanguage === "es" ? "es" : "en");
  }, [calendarLanguage]);
  useEffect(() => {
    const updateHeight = () => {
      if (calendarContainerRef.current) {
        const top = calendarContainerRef.current.getBoundingClientRect().top;
        setCalendarHeight(Math.max(560, window.innerHeight - top - 24));
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [isLoadingOpenDays]);
  const modalTitle = mode === "edit" ? t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.editShift") : selectedOption === "closed_exception" ? t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.createOneOffClosure") : selectedOption === "open_exception" ? t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.createOneOffShift") : t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.createRegularShift");
  useEffect(() => {
    dispatch(fetchRestaurantOpenDays());
    dispatch(fetchOpenClosedExceptions(restaurant_id));
  }, [dispatch, restaurant_id]);
  useEffect(() => {
    if (!Array.isArray(openDays)) return;
    const weekdaysMap = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 0
    };
    const firstDayOfMonth = currentDate.clone().startOf("month");
    const lastDayOfMonth = currentDate.clone().endOf("month");
    const formattedOpenDaysEvents = openDays.flatMap(day => Object.keys(weekdaysMap).reduce((acc, weekday) => {
      if (day[weekday]) {
        let startDate = firstDayOfMonth.clone().day(weekdaysMap[weekday]);
        if (startDate.isBefore(firstDayOfMonth)) {
          startDate.add(7, "days");
        }
        const zonesFormatted = (day.list_of_zones || "").split(",").filter(Boolean).join("\n");
        const formattedStart = moment(day.start_time, "HH:mm:ss").format("HH:mm");
        const formattedEnd = moment(day.end_time, "HH:mm:ss").format("HH:mm");
        while (startDate.isSameOrBefore(lastDayOfMonth)) {
          acc.push({
            id: day.id,
            title: `${formattedStart} - ${formattedEnd}${zonesFormatted ? `\n${zonesFormatted}` : ""}`,
            start: moment(`${startDate.format("YYYY-MM-DD")} ${day.start_time}`, "YYYY-MM-DD HH:mm:ss").toDate(),
            end: moment(`${startDate.format("YYYY-MM-DD")} ${day.end_time}`, "YYYY-MM-DD HH:mm:ss").toDate(),
            type: "regular_shift",
            allDay: false
          });
          startDate.add(7, "days");
        }
      }
      return acc;
    }, []));
    const formattedExceptionsEvents = Array.isArray(exceptions) ? exceptions.flatMap(exception => {
      const events = [];
      const startDate = moment(exception.closed_days_start, "YYYY-MM-DD");
      const endDate = exception.closed_days_end ? moment(exception.closed_days_end, "YYYY-MM-DD") : startDate;
      const eventStartTime = exception.start_time || "00:00:00";
      const eventEndTime = exception.end_time || "23:59:00";
      const startFormatted = moment(eventStartTime, "HH:mm:ss").format("HH:mm");
      const endFormatted = moment(eventEndTime, "HH:mm:ss").format("HH:mm");
      const timeRange = startFormatted === "00:00" && endFormatted === "23:59" ? "" : `${startFormatted} - ${endFormatted}`;
      const zonesFormatted = (exception.list_of_zones || "").split(",").filter(Boolean).join("\n");
      const titlePrefix = exception.type_of_exception === "closed_exception" ? t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.closed") : exception.type_of_exception === "open_exception" ? t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.openException") : t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.limited");
      let current = startDate.clone();
      while (current.isSameOrBefore(endDate)) {
        events.push({
          id: exception.id,
          title: `${timeRange ? `${timeRange}\n` : ""}${zonesFormatted || titlePrefix}`,
          start: moment(`${current.format("YYYY-MM-DD")} ${eventStartTime}`, "YYYY-MM-DD HH:mm:ss").toDate(),
          end: moment(`${current.format("YYYY-MM-DD")} ${eventEndTime}`, "YYYY-MM-DD HH:mm:ss").toDate(),
          type: exception.type_of_exception,
          allDay: startFormatted === "00:00" && endFormatted === "23:59"
        });
        current.add(1, "day");
      }
      return events;
    }) : [];
    setEvents([...formattedOpenDaysEvents, ...formattedExceptionsEvents]);
  }, [openDays, exceptions, currentDate, calendarLanguage, t]);
  const handleSelectSlot = useCallback(({
    start,
    end
  }) => {
    setMode("create");
    setSelectedOption("regular_shift");
    setSelected_obj_id(null);
    setModalIsOpen(true);
  }, []);
  const handleSelectEvent = useCallback(event => {
    setMode("edit");
    setSelected_obj_id(event.id);
    setSelectedOption(event.type);
    setModalIsOpen(true);
  }, []);
  if (isLoadingOpenDays) return <div>{t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.loading")}</div>;
  return <>

      <div className="flex justify-between items-center mb-2">
        <ShiftActions>
          <ShiftButton type="button" $kind="regular_shift" onClick={() => {
          setSelectedOption("regular_shift");
          setMode("create");
          setSelected_obj_id(null);
          setModalIsOpen(true);
        }}><LuRepeat2 />{t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.turnoRegular")}</ShiftButton>

          <ShiftButton type="button" $kind="open_exception" onClick={() => {
          setSelectedOption("open_exception");
          setMode("create");
          setSelected_obj_id(null);
          setModalIsOpen(true);
        }}><LuCalendarPlus />{t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.turnoPontual")}</ShiftButton>

          <ShiftButton type="button" $kind="closed_exception" onClick={() => {
          setModalIsOpen(true);
          setMode("create");
          setSelectedOption("closed_exception");
        }}><LuCalendarX2 />{t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.fechoPontual")}</ShiftButton>
        </ShiftActions>
      </div>

      <ScheduleCalendar ref={calendarContainerRef} $height={calendarHeight}>
        <Calendar style={{
        border: "none"
      }} onView={view => {
        setCalendarView(view);
        if (view === "week") {
          setTimeout(() => {
            if (!calendarContainerRef.current) return;
            const timeContent = calendarContainerRef.current.querySelector(".rbc-time-view");
            const events = calendarContainerRef.current.querySelectorAll(".rbc-time-content .rbc-event");
            if (timeContent && events.length > 0) {
              // Get the earliest event visually
              let earliestTop = Infinity;
              events.forEach(event => {
                if (event.offsetTop < earliestTop) {
                  earliestTop = event.offsetTop;
                }
              });

              // Scroll to just above the earliest event
              timeContent.scrollTop = Math.max(earliestTop - 20, 0);
            }
          }, 100); // Give time for layout/rendering
        }
      }} localizer={localizer} events={events} startAccessor="start" endAccessor="end" selectable onSelectSlot={handleSelectSlot} onSelectEvent={handleSelectEvent} onNavigate={date => setCurrentDate(moment(date))} culture={calendarLanguage} min={new Date(new Date().setHours(0, 0, 0, 0))} max={new Date(new Date().setHours(23, 59, 59, 999))} defaultView="month" views={["month", "week"]} messages={{
        today: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.today"),
        previous: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.previous"),
        next: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.next"),
        month: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.month"),
        week: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.week"),
        day: t("ui.components.restaurantSchedule.restaurantCalendarOpenClosedDays.day")
      }} components={{
        toolbar: CalendarCustomToolbar,
        event: ScheduleEvent
      }} eventPropGetter={scheduleEventAppearance} popup />
      </ScheduleCalendar>

      <CustomModal show={modalIsOpen} onHide={() => setModalIsOpen(false)} title={modalTitle} centered={false}>
        <div style={{
        overflow: "visible",
        position: "relative",
        zIndex: 1200
      }}>
          {selectedOption === "regular_shift" && <TimeManagement shiftId={selected_obj_id} mode={mode} restaurantId={restaurant_id} showModal={modalIsOpen} setShowModal={setModalIsOpen} />}

          {(selectedOption === "open_exception" || selectedOption === "closed_exception" || selectedOption === "limited") && <OpenClosedExceptions exception_id={selected_obj_id} type_of_exception={selectedOption} mode={mode} restaurant_id={restaurant_id} setShowModal={setModalIsOpen} />}
        </div>
      </CustomModal>
    </>;
};
export default RestaurantCalendarOpenClosedDays;
